export const DASHBOARD_ROOT = "/dashboard";

const isOverviewPath = (addressLink) =>
    Boolean(addressLink) &&
    addressLink.replace(/\/+$/, "") === DASHBOARD_ROOT;

export const APP_FLAVOR_USER = "user";
export const APP_FLAVOR_ORGAN = "organ";
export const APP_FLAVOR_STORAGE_KEY = "app_flavor";
export const APP_TABBAR_MAX_WIDTH = 879;

const USER_MESSAGE_PATH = `${DASHBOARD_ROOT}/messages/user`;
const ORGAN_MESSAGE_PATH = `${DASHBOARD_ROOT}/messages/organ`;

export const ORGAN_BOTTOM_NAV_DEFS = [
    {
        key: "messages",
        addressLink: ORGAN_MESSAGE_PATH,
        labelKey: "AppNav.Organ.Messages",
    },
    {
        key: "surveys",
        addressLink: `${DASHBOARD_ROOT}/surveys`,
        labelKey: "AppNav.Organ.Surveys",
    },
    {
        key: "experience",
        addressLink: `${DASHBOARD_ROOT}/exp-settings`,
        labelKey: "AppNav.Organ.Experience",
    },
    {
        key: "members",
        addressLink: `${DASHBOARD_ROOT}/customer-club`,
        labelKey: "AppNav.Organ.Members",
        search: "?tab=members",
    },
];

export const USER_BOTTOM_NAV_DEFS = [
    {
        key: "messages",
        addressLink: USER_MESSAGE_PATH,
        labelKey: "AppNav.User.Messages",
    },
    {
        key: "participation",
        addressLink: `${DASHBOARD_ROOT}/user/customer-club`,
        labelKey: "AppNav.User.Participation",
    },
    {
        key: "scan",
        addressLink: "/scanner",
        labelKey: "AppNav.User.Scan",
        featured: true,
    },
    {
        key: "surveys",
        addressLink: `${DASHBOARD_ROOT}/user/poll`,
        labelKey: "AppNav.User.Surveys",
    },
    {
        key: "rewards",
        addressLink: `${DASHBOARD_ROOT}/user/discount`,
        labelKey: "AppNav.User.Rewards",
    },
];

export const normalizeAppPath = (path) => {
    if (!path) return "/";
    const trimmed = String(path).split("?")[0].split("#")[0].replace(/\/+$/, "");
    return trimmed || "/";
};

const readStorage = (storage) => {
    if (storage) return storage;
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

export const isValidAppFlavor = (value) =>
    value === APP_FLAVOR_USER || value === APP_FLAVOR_ORGAN;

export const isOrganAccount = (user) => {
    if (!user) return false;
    const level = Number(user.user_level ?? user.usertype);
    return level === 1 || level === 2 || user.is_superuser === true;
};

export const getAppQueryFlavor = (search) => {
    const params = new URLSearchParams(search || "");
    const value = params.get("app");
    return isValidAppFlavor(value) ? value : null;
};

export const getAppFlavorFromPath = (pathname) => {
    const path = pathname || "";
    if (
        path === "/scanner" ||
        path.startsWith("/scanner/")
    ) {
        return null;
    }
    if (
        path.includes("/dashboard/user") ||
        path.includes("/dashboard/messages/user")
    ) {
        return APP_FLAVOR_USER;
    }
    if (path.startsWith("/dashboard") || path.includes("/dashboard/")) {
        return APP_FLAVOR_ORGAN;
    }
    return null;
};

export const readStoredAppFlavor = (storage) => {
    const value = readStorage(storage)?.getItem(APP_FLAVOR_STORAGE_KEY);
    return isValidAppFlavor(value) ? value : null;
};

export const persistAppFlavor = (flavor, storage) => {
    if (!isValidAppFlavor(flavor)) return flavor;
    readStorage(storage)?.setItem(APP_FLAVOR_STORAGE_KEY, flavor);
    return flavor;
};

export const isStandaloneDisplay = (windowRef) => {
    const source = windowRef || (typeof window !== "undefined" ? window : null);
    if (!source) return false;
    try {
        return (
            source.matchMedia?.("(display-mode: standalone)")?.matches ===
                true || source.navigator?.standalone === true
        );
    } catch {
        return false;
    }
};

export const resolveAppFlavor = ({
    pathname = "",
    search = "",
    storage,
    standalone,
} = {}) => {
    const fromQuery = getAppQueryFlavor(search);
    if (fromQuery) {
        persistAppFlavor(fromQuery, storage);
        return fromQuery;
    }
    const stored = readStoredAppFlavor(storage);
    const installed =
        standalone === undefined ? isStandaloneDisplay() : standalone;
    if (installed && stored) {
        return stored;
    }
    const fromPath = getAppFlavorFromPath(pathname);
    if (fromPath) return fromPath;
    return stored || APP_FLAVOR_USER;
};

export const getPostLoginFlavor = (user, requestedFlavor) => {
    if (requestedFlavor === APP_FLAVOR_ORGAN && !isOrganAccount(user)) {
        return APP_FLAVOR_USER;
    }
    if (isValidAppFlavor(requestedFlavor)) {
        return requestedFlavor;
    }
    return isOrganAccount(user) ? APP_FLAVOR_ORGAN : APP_FLAVOR_USER;
};

export const shouldShowPanelBottomNav = (pathname) => {
    const path = pathname || "";
    if (path === "/auth" || path.startsWith("/auth/")) return false;
    if (path.includes("/content/share/")) return false;
    if (path.includes("/feedback")) return false;
    if (path.startsWith("/survey/")) return false;
    if (path === "/scanner" || path.startsWith("/scanner/")) return true;
    return path.startsWith("/dashboard");
};

export const matchesNavLink = (itemLink, pathname, exact = false) => {
    const path = normalizeAppPath(pathname);
    const to = normalizeAppPath(itemLink);
    if (exact) {
        return path === to || path === DASHBOARD_ROOT;
    }
    return path === to || path.startsWith(`${to}/`);
};

export const isBottomNavItemActive = (item, pathname) =>
    matchesNavLink(item?.to, pathname, item?.exact);

const findSidebarItem = (sidebarList, addressLink, exact) => {
    const target = normalizeAppPath(addressLink);
    return (sidebarList || []).find((entry) => {
        if (exact) {
            return isOverviewPath(entry.addressLink);
        }
        return normalizeAppPath(entry.addressLink) === target;
    });
};

export const buildOrganBottomNavItems = ({ sidebarList, t, scanIcon = null }) => {
    const sideItems = ORGAN_BOTTOM_NAV_DEFS.map((def) => {
        const match = findSidebarItem(
            sidebarList,
            def.addressLink,
            def.exact
        );
        if (!match) return null;
        return {
            key: def.key,
            to: `${match.addressLink}${def.search || ""}`,
            label: t(def.labelKey),
            icon: match.iconName || match.icon,
            exact: Boolean(def.exact),
        };
    }).filter(Boolean);
    const middle = Math.max(1, Math.floor(sideItems.length / 2));
    const scanItem = {
        key: "scan",
        to: "/scanner",
        label: t("AppNav.User.Scan"),
        icon: scanIcon,
        featured: true,
        exact: true,
    };
    return [
        ...sideItems.slice(0, middle),
        scanItem,
        ...sideItems.slice(middle),
    ];
};

export const buildUserBottomNavItems = ({ t, icons = {} }) => {
    const sideItems = USER_BOTTOM_NAV_DEFS.filter((def) => !def.featured).map(
        (def) => ({
            key: def.key,
            to: def.addressLink,
            label: t(def.labelKey),
            icon: icons[def.key] || null,
            exact: false,
        })
    );
    const scanDef = USER_BOTTOM_NAV_DEFS.find((def) => def.featured);
    const scanItem = {
        key: scanDef.key,
        to: scanDef.addressLink,
        label: t(scanDef.labelKey),
        icon: icons.scan || null,
        featured: true,
        exact: true,
    };
    const middle = Math.ceil(sideItems.length / 2);
    return [
        ...sideItems.slice(0, middle),
        scanItem,
        ...sideItems.slice(middle),
    ];
};

export const applyAppFlavorToDocument = (flavor) => {
    if (typeof document === "undefined") return;
    document.body.dataset.appFlavor = flavor;
    document.documentElement.dataset.appFlavor = flavor;
};
